import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input, DatePicker, Select, Radio, Checkbox } from "antd";
import { GetAllCountryApi } from "../../../../api/request/common.js";
import {
  AddUpdateRepresentativeInfoApi,
  getRepresentativeInfoApi,
  getTaxDomicilesReasons,
} from "../../../../api/request/corporate.js";
import api from "../../../../api/index.js";
import toast from "react-hot-toast";
import { handleApiError } from "../../../../utils/handleApiError.js";
import Dragger from "antd/es/upload/Dragger";
import TableWithSkeleton from "../../../../components/table/TableWithSkeleton.jsx";
import dayjs from "dayjs";
import Loader from "../../../../components/Loader.jsx";
import { ProofTypeOptions } from "../../../../utils/constant.js";
import { useColumns } from "../../../../columns/documentColumns.jsx";
import {
  DocumentUploadButton,
  mobileNumberValidator,
} from "../../../../utils/utils.js";
import { useTranslation } from "../../../../utils/onboardingI18n";
import AlternateTaxIdentifierField from "../../../../components/onboarding/AlternateTaxIdentifierField.jsx";

const { TextArea } = Input;

const RepresentativeInfo = ({ onNext, onPrevious, id, isSubmitted }) => {
  const { t } = useTranslation("onBoarding");
  const [form] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [isUSPerson, setIsUSPerson] = useState(null);
  const [acceptDeclaration, setAcceptDeclaration] = useState(false);
  const [hasOtherCrsTaxResident, setHasOtherCrsTaxResident] = useState(false);
  const [isPEP, setIsPEP] = useState(null);
  const [documentFileList, setDocumentFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [proofType, setProofType] = useState(null);
  const [documentSubType, setDocumentSubType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reasons, setReasons] = useState([]);
  const savedSocialSecurityNumberRef = useRef("");
  const isDisabled = isSubmitted;

  const getCountries = async () => {
    try {
      const { data } = await GetAllCountryApi();
      if (data?.success) {
        setCountries(
          data?.data?.map((item, i) => ({
            key: i,
            value: item?.id,
            label: item?.countryName,
          }))
        );

        setCountryCodes(
          data?.data
            ?.map((item, i) => ({
              key: i,
              value: (item?.dialCode || item?.countryCode1),
              label: `${(item?.dialCode || item?.countryCode1)} (${item?.countryCode2})`,
              countryCode: (item?.dialCode || item?.countryCode1),
            }))
            ?.filter((item) => item?.value)
        );
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const getRepresentativeInfo = async () => {
    try {
      const { data } = await getRepresentativeInfoApi(id);
      if (data?.success) {
        const info = data?.data || {};
        const phone = info?.phoneNumber ?? "";
        const [countryCode, phoneNumber] = phone.trim().split(" ");
        const socialSecurityNumber = info?.socialSecurityNumber || "";
        savedSocialSecurityNumberRef.current = socialSecurityNumber;
        setIsUSPerson(
          typeof info?.areYouUSPerson === "boolean" ? info.areYouUSPerson : null
        );
        setIsPEP(info?.isPoliticallyExposedPerson || false);
        form.setFieldsValue({
          ...info,
          areYouUSPerson: info?.areYouUSPerson,
          socialSecurityNumber,
          dob: info?.dob ? dayjs(info.dob) : null,
          phoneCountryCode: countryCode || null,
          phoneNumber: phoneNumber || null,
        });
        const docs = Array.isArray(data?.data?.documents)
          ? data.data.documents
          : [];
        const mapped = docs.map((d) => {
          const selectedProof = ProofTypeOptions.find(
            (o) => o.value === d.documentType
          );
          const typeLabel = selectedProof?.label || `${d.documentType}`;
          const subLabel =
            (selectedProof?.subTypeOptions || []).find(
              (o) => o.value === d.documentSubType
            )?.label || `${d.documentSubType}`;
          return {
            id: d.id || Date.now(),
            documentType: typeLabel,
            documentName: subLabel,
            fileName: d.fileName || "document",
            file: { url: d.fileUrlPath },
            status: "UPLOADED",
            proofType: d.documentType,
            subType: d.documentSubType,
          };
        });
        setUploadedDocuments(mapped);
      }
    } catch (error) {
      console.error("Error fetching representative info:", error);
    }
  };

  const getAllReasons = async () => {
    try {
      const { data } = await getTaxDomicilesReasons();
      if (data?.success) {
        setReasons(
          data?.data?.map((item) => ({ value: item?.id, label: item?.name }))
        );
      }
    } catch (error) {}
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const promises = [getCountries(), getAllReasons()];

        if (id) promises.push(getRepresentativeInfo());

        await Promise.all(promises);
      } catch (error) {
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [id]);

  useEffect(() => {
    if (isLoading || isUSPerson !== true) return;
    const socialSecurityNumber = savedSocialSecurityNumberRef.current;
    if (!socialSecurityNumber) return;
    form.setFieldsValue({ socialSecurityNumber });
  }, [isLoading, isUSPerson, form]);

  const filterOption = (input, option) => {
    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  };

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    try {
      const taxDomicilesArray = values?.taxDomiciles || [];
      delete values?.taxDomiciles;
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append(
        "phoneNumber",
        `${values?.phoneCountryCode || ""} ${values?.phoneNumber || ""}`.trim()
      );
      delete values?.phoneCountryCode;
      delete values?.acceptDeclaration;
      delete values?.phoneNumber;
      Object.keys(values || {}).forEach((key) => {
        const val = values[key];
        if (
          val &&
          typeof val === "object" &&
          typeof val.format === "function"
        ) {
          formData.append(key, val);
        } else {
          formData.append(key, String(val ?? ""));
        }
      });

      uploadedDocuments.forEach((doc, index) => {
        const fileObj = doc?.file?.originFileObj || doc?.file;
        const idToUse = doc?.id;
        if (fileObj instanceof Blob) {
          const fname = fileObj?.name || doc?.fileName || "document";
          formData.append(`documents[${index}].file`, fileObj, fname);
          formData.append(`documents[${index}].fileUrlPath`, "");
        } else {
          formData.append(`documents[${index}].id`, idToUse);
          const urlPath = (doc?.file && doc.file.url) || doc?.fileUrlPath || "";
          formData.append(`documents[${index}].fileUrlPath`, urlPath);
        }
        formData.append(`documents[${index}].documentType`, doc.proofType);
        formData.append(`documents[${index}].documentSubType`, doc.subType);
      });
      taxDomicilesArray.forEach((item, index) => {
        formData.append(
          `taxDomiciles[${index}].taxDomicileCountryId`,
          String(item?.taxDomicileCountryId ?? "")
        );
        formData.append(
          `taxDomiciles[${index}].isTINIDAvailable`,
          String(item?.isTINIDAvailable ?? false)
        );
        if (item?.isTINIDAvailable) {
          formData.append(
            `taxDomiciles[${index}].taxDomicileReasonID`,
            String(item?.taxDomicileReasonID ?? "")
          );
          formData.append(`taxDomiciles[${index}].taxIDNumber`, "");
          formData.append(
            `taxDomiciles[${index}].alternateTaxIdentifier`,
            String(item?.alternateTaxIdentifier ?? "")
          );
        } else {
          formData.append(`taxDomiciles[${index}].taxDomicileReasonID`, "");
          formData.append(
            `taxDomiciles[${index}].taxIDNumber`,
            String(item?.taxIDNumber ?? "")
          );
        }
      });
      id && formData.append("id", id);
      const { data } = await AddUpdateRepresentativeInfoApi(formData);
      if (data?.success) {
        toast.success(data?.message || t("Representative info saved"));
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUSPersonChange = (e) => {
    const value = e.target.value;
    setIsUSPerson(value);
    setAcceptDeclaration(false);
    if (value) {
      form.setFieldsValue({
        socialSecurityNumber: savedSocialSecurityNumberRef.current || "",
      });
    }
  };

  const handleMultiTaxResidentChange = (e) => {
    const isMultiTaxDomicile = e?.target?.value;
    setHasOtherCrsTaxResident(isMultiTaxDomicile);
    const taxDomiciles = form.getFieldValue("taxDomiciles") || [];

    if (isMultiTaxDomicile) {
      if (!taxDomiciles?.length) {
        form.setFieldsValue({
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
            },
          ],
        });
      }
    } else {
      if (taxDomiciles?.length) {
        form.setFieldsValue({
          taxDomiciles: [taxDomiciles[0]],
        });
      }
    }
  };

  const handlePEPChange = (e) => {
    const value = e.target.value;
    setIsPEP(value);
    if (value) {
      form.setFieldsValue({ pepDetails: "" });
    }
  };

  const handleDocumentChange = ({ fileList }) => {
    if (!fileList.length) return;

    const fileObj = fileList[0]?.originFileObj;
    if (!fileObj) return;

    setDocumentFileList(fileList);
    setSelectedFile(fileObj);
    setPreviewUrl(URL.createObjectURL(fileObj));
  };
  
  const handleAddDocument = (e) => {
    e.preventDefault();
    if (!selectedFile || !proofType || !documentSubType) return;

    addToUploadedDocuments(proofType, documentSubType, {
      originFileObj: selectedFile,
    });

    setSelectedFile(null);
    setPreviewUrl(null);
    setDocumentFileList([]);
    form.setFieldsValue({ proofType: null });
    form.setFieldsValue({ documentSubType: null });
  };

  const handleRemoveDocument = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setDocumentFileList([]);
  };

  const handleProofTypeChange = (value) => {
    setProofType(value);
    form.setFieldsValue({ proofType: value });
    setDocumentSubType(null);
    form.setFieldsValue({ documentSubType: null });
  };

  const handleDocumentSubTypeChange = (value) => {
    setDocumentSubType(value);
    form.setFieldsValue({ documentSubType: value });
  };

  const addToUploadedDocuments = (proofTypeValue, subTypeValue, file) => {
    const selectedProof = ProofTypeOptions.find(
      (opt) => opt.value === proofTypeValue
    );
    const proofTypeLabel = selectedProof?.label || proofTypeValue;
    const subTypeLabel =
      (selectedProof?.subTypeOptions || []).find(
        (opt) => opt.value === subTypeValue
      )?.label || subTypeValue;

    const newDoc = {
      id: Date.now(),
      documentType: proofTypeLabel,
      documentName: subTypeLabel,
      fileName: file.originFileObj?.name || file.name,
      file: file,
      status: "PROCESSING",
      proofType: proofTypeValue,
      subType: subTypeValue,
    };

    setUploadedDocuments((prev) => {
      const filtered = prev.filter(
        (doc) =>
          !(doc.proofType === proofTypeValue && doc.subType === subTypeValue)
      );
      return [...filtered, newDoc];
    });
  };

  const handleDeleteDocument = (id) => {
    if (isDisabled) return;
    setUploadedDocuments((prev) => prev.filter((doc) => doc.id !== id));
    const deletedDoc = uploadedDocuments.find((doc) => doc.id === id);
    if (deletedDoc && documentFileList.length > 0) {
      const currentFile = documentFileList[0];
      if (currentFile.originFileObj?.name === deletedDoc.fileName) {
        setDocumentFileList([]);
      }
    }
  };

  const handleDownload = async (file, suggestedName) => {
    if (file.originFileObj) {
      const url = URL.createObjectURL(file.originFileObj);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.originFileObj.name;
      link.click();
      URL.revokeObjectURL(url);
      return;
    }
    if (file.url) {
      try {
        const response = await api.get(file.url, { responseType: "blob" });
        const blobUrl = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = suggestedName || "document";
        link.click();
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        handleApiError(err);
      }
    }
  };

  const handleAction = (action, record) => {
    if (action === "download") {
      handleDownload(record.file, record.fileName);
    } else if (action === "delete") {
      handleDeleteDocument(record.id);
    }
  };

  const columns = useColumns(handleAction, uploadedDocuments?.length);

  if (isLoading) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto ">
      <Form
        form={form}
        size="large"
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        validateTrigger={["onChange", "onBlur", "onSubmit"]}
        disabled={isDisabled}
        initialValues={{
          isMultiTaxDomicileEnabled: false,
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
            },
          ],
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto border border-gray-300 rounded-lg p-6 mb-6">
          <Form.Item
            label={t("First Name/Given Name")}
            name="firstName"
            rules={[{ required: true, message: t("Please enter first name") }]}
          >
            <Input placeholder={t("Enter first name")} />
          </Form.Item>

          <Form.Item
            label={t("Last Name/Surname")}
            name="lastName"
            rules={[{ required: true, message: t("Please enter last name") }]}
          >
            <Input placeholder={t("Enter last name")} />
          </Form.Item>

          <Form.Item
            label={t("Date Of Birth")}
            name="dob"
            rules={[{ required: true, message: t("Please select date of birth") }]}
          >
            <DatePicker
              className="w-full"
              placeholder={t("DD/MM/YYYY")}
              format="DD/MM/YYYY"
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
            />
          </Form.Item>

          <Form.Item
            label={t("Email Id")}
            name="email"
            rules={[
              { required: true, message: t("Please enter email") },
              { type: "email", message: t("Please enter a valid email") },
            ]}
          >
            <Input type="email" placeholder={t("Enter email")} />
          </Form.Item>

          <Form.Item label={t("Phone Number")} required>
            <div className="flex w-full gap-2">
              <Form.Item
                name="phoneCountryCode"
                noStyle
                rules={[{ required: true, message: t("Country code required") }]}
              >
                <Select
                  showSearch
                  style={{ width: "30%" }}
                  filterOption={filterOption}
                  placeholder={t("Code")}
                  options={countryCodes}
                />
              </Form.Item>
              <Form.Item
                name="phoneNumber"
                noStyle
                dependencies={["phoneCountryCode"]}
                rules={[mobileNumberValidator()]}
              >
                <Input
                  style={{ width: "70%" }}
                  placeholder={t("Enter phone number")}
                  maxLength={15}
                  inputMode="numeric"
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </div>
          </Form.Item>

          {/* Nationality */}
          <Form.Item
            label={t("Nationality 1")}
            name="nationality"
            rules={[{ required: true, message: t("Please select nationality") }]}
          >
            <Select
              showSearch
              placeholder={t("Please select nationality")}
              filterOption={filterOption}
              options={countries}
            />
          </Form.Item>

          {/* Place of Birth */}
          <Form.Item
            label={t("Place of Birth")}
            name="placeOfBirth"
            rules={[
              { required: true, message: t("Please select place of birth") },
            ]}
          >
            <Select
              showSearch
              placeholder={t("Select place of birth")}
              filterOption={filterOption}
              options={countries}
            />
          </Form.Item>

          {/* Country of residence */}
          <Form.Item
            label={t("Country of residence")}
            name="countryOfResidence"
            rules={[
              { required: true, message: t("Please select country of residence") },
            ]}
          >
            <Select
              showSearch
              placeholder={t("Select country of residence")}
              filterOption={filterOption}
              options={countries}
            />
          </Form.Item>

          {/* Country of Tax Residence */}
          <Form.Item
            label={t("Country of Tax Residence")}
            name="countryOfTaxResidence"
            rules={[
              {
                required: true,
                message: t("Please select country of tax residence"),
              },
            ]}
          >
            <Select
              showSearch
              placeholder={t("Select country of tax residence")}
              filterOption={filterOption}
              options={countries}
            />
          </Form.Item>
        </div>

        {/* Residential address Section */}
        <div className="max-w-5xl mx-auto border border-gray-300 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">{t("Residential address")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Apartment/Villa Number/Name */}
            <Form.Item
              label="Apartment/Villa Number/Name"
              name="crApartmentVillaInfo"
              rules={[
                {
                  required: true,
                  message: "Please enter apartment/villa number/name",
                },
              ]}
            >
              <Input placeholder="Enter apartment/villa number/name" />
            </Form.Item>

            {/* Street Name/Area */}
            <Form.Item
              label="Street Name/Area"
              name="crStreetAreaInfo"
              rules={[
                { required: true, message: "Please enter street name/area" },
              ]}
            >
              <Input placeholder="Enter street name/area" />
            </Form.Item>

            {/* Country */}
            <Form.Item
              label="Country"
              name="residenceCountry"
              rules={[{ required: true, message: "Please select country" }]}
            >
              <Select
                showSearch
                placeholder="Select country"
                filterOption={filterOption}
                options={countries}
              />
            </Form.Item>

            {/* City / Town */}
            <Form.Item
              label="City / Town"
              name="cityTown"
              rules={[{ required: true, message: "Please enter city/town" }]}
            >
              <Input placeholder="Enter city/town" />
            </Form.Item>

            {/* Postal/Zip Code */}
            <Form.Item
              label="Postal/Zip Code"
              name="postalZipCode"
              rules={[
                { required: true, message: "Please enter postal/zip code" },
              ]}
            >
              <Input placeholder="Enter postal/zip code" />
            </Form.Item>
          </div>
        </div>

        {/* Tax Information Section */}
        <div className="max-w-5xl mx-auto border border-gray-300 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">{t("Tax Information")}</h3>

          {/* FATCA and CRS Section */}
          <div className="mb-6">
            <h4 className="text-base font-semibold mb-2">{t("FATCA and CRS")}</h4>
            <p className="text-sm text-gray-600 mb-4">
              {t("In accordance with the Foreign Account Tax Compliance Act, 2010 (FATCA), please answer the following questions:")}
            </p>

            {/* Are you a U.S. Person? */}
            <Form.Item
              label={t("Are you a U.S. Person?")}
              name="areYouUSPerson"
              rules={[{ required: true, message: t("Please select an option") }]}
            >
              <Radio.Group onChange={handleUSPersonChange}>
                <Radio value={true}>{t("Yes")}</Radio>
                <Radio value={false}>{t("No")}</Radio>
              </Radio.Group>
            </Form.Item>

            {isUSPerson !== null && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-4">
                  {t("Please note this also includes if you are a US green Card holder, hold a US bank account, have a US PO box or postal address, have a US based power of attorney etc. Please visit the website www.irs.gov for full details or consult a foreign tax advisor.")}
                </p>

                {/* Declaration Box */}
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 mb-4">
                  <p className="text-sm text-gray-700 mb-3">
                    {isUSPerson
                      ? t("I declare that I AM a U.S. Person and am subject to U.S. federal income taxes. I, hereby represent that, and by checking the applicable box below, the information provided by me is true and accurate.")
                      : t("I declare that I AM not a U.S. Person and am subject to U.S. federal income taxes. I, hereby represent that, and by checking the applicable box below, the information provided by me is true and accurate.")}
                  </p>
                  <Form.Item
                    name="acceptDeclaration"
                    valuePropName="checked"
                    rules={[
                      {
                        validator: (_, value) =>
                          value
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(t("Please accept the declaration"))
                              ),
                      },
                    ]}
                  >
                    <Checkbox>{t("I accept above declaration")}</Checkbox>
                  </Form.Item>
                </div>

                {/* Social Security Number (only for US Person) */}
                {isUSPerson && (
                  <Form.Item
                    label={t("Tax Identification Number")}
                    name="socialSecurityNumber"
                    rules={[
                      {
                        required: true,
                        message: t("Please enter social security number"),
                      },
                    ]}
                  >
                    <Input placeholder={t("Enter social security number")} />
                  </Form.Item>
                )}
              </div>
            )}
          </div>

          <div className="mt-6">
            <Form.Item
              label={t("For Common Reporting Standard (CRS) purposes, please mention which countries you are tax resident of?")}
              name="crsTaxResident"
            ></Form.Item>

            <div className="border border-gray-300 rounded-lg p-6 mt-6">
              <div className="mb-6">
                <Form.Item
                  label={t("Are there any other countries you are tax resident of?")}
                  name="isMultiTaxDomicileEnabled"
                  rules={[
                    { required: true, message: t("Please select an option") },
                  ]}
                >
                  <Radio.Group onChange={handleMultiTaxResidentChange}>
                    <Radio value={true}>{t("Yes")}</Radio>
                    <Radio value={false}>{t("No")}</Radio>
                  </Radio.Group>
                </Form.Item>
              </div>

              <Form.List name="taxDomiciles">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <div
                        key={field.key}
                        className="border border-gray-300 rounded-lg p-6 mt-6"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-semibold">
                            {index === 0
                              ? t("Tax Resident Information")
                              : `${t("Additional Tax Resident Information")} ${index}`}
                          </h3>

                          {index !== 0 && (
                            <Button
                              type="link"
                              danger
                              onClick={() => remove(field.name)}
                            >
                              {t("Remove")}
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Form.Item
                            label={t("Select country")}
                            name={[field.name, "taxDomicileCountryId"]}
                            rules={[
                              {
                                required: true,
                                message: t("Please select country"),
                              },
                            ]}
                          >
                            <Select
                              placeholder={t("Select country")}
                              options={countries}
                            />
                          </Form.Item>

                          <Form.Item shouldUpdate>
                            {({ getFieldValue }) => {
                              const noTIN = getFieldValue([
                                "taxDomiciles",
                                index,
                                "isTINIDAvailable",
                              ]);

                              return (
                                <Form.Item
                                  label={t("Tax Identification Number")}
                                  name={[field.name, "taxIDNumber"]}
                                  rules={
                                    noTIN
                                      ? []
                                      : [
                                          {
                                            required: true,
                                            message: t("Enter TIN"),
                                          },
                                        ]
                                  }
                                  disabled={noTIN || isDisabled}
                                  className="w-full"
                                >
                                  <Input
                                    placeholder={t("Enter tax identification number")}
                                    disabled={noTIN || isDisabled}
                                  />
                                </Form.Item>
                              );
                            }}
                          </Form.Item>

                          <Form.Item
                            name={[field.name, "isTINIDAvailable"]}
                            valuePropName="checked"
                            className="md:col-span-2"
                          >
                            <Checkbox>{t("Check if you don't have TIN?")}</Checkbox>
                          </Form.Item>

                          <Form.Item className="md:col-span-2" shouldUpdate>
                            {({ getFieldValue }) => {
                              const noTIN = !getFieldValue([
                                "taxDomiciles",
                                index,
                                "isTINIDAvailable",
                              ]);

                              if (noTIN) return null;

                              return (
                                <Form.Item
                                  label={t("Please select a reason")}
                                  name={[field.name, "taxDomicileReasonID"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: t("Please select reason"),
                                    },
                                  ]}
                                >
                                  <Radio.Group>
                                    {reasons.map((r) => (
                                      <Radio
                                        key={r.value}
                                        value={r.value}
                                        className="block mb-2"
                                      >
                                        {r.label}
                                      </Radio>
                                    ))}
                                  </Radio.Group>
                                </Form.Item>
                              );
                            }}
                          </Form.Item>

                          <AlternateTaxIdentifierField
                            field={field}
                            index={index}
                            label={t("ID or Passport Number")}
                            isDisabled={isDisabled}
                            t={t}
                          />
                        </div>
                      </div>
                    ))}

                    {hasOtherCrsTaxResident && (
                      <div className="mt-6">
                        <Button
                          type="dashed"
                          block
                          onClick={() =>
                            add({
                              taxDomicileCountryId: null,
                              taxIDNumber: "",
                              isTINIDAvailable: false,
                              taxDomicileReasonID: null,
                              alternateTaxIdentifier: "",
                            })
                          }
                        >
                          {t("+ Add Another Tax Resident Country")}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </Form.List>
            </div>
          </div>
        </div>

        {/* Politically Exposed Person Section */}
        <div className="max-w-5xl mx-auto border border-gray-300 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-primary">
            {t("Politically Exposed Person? *")}
          </h3>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              {t("PEP shall include Heads of States or Governments, senior politicians, senior government officials, judicial or military officials, senior executive managers of state owned corporations, and senior officials of political parties and persons who are, or have previously been, entrusted with the management of an international organisation or any prominent function within such an organisation; and the definition also includes the following:")}
            </p>

            <div className="ml-4 mb-2">
              <p className="text-sm text-gray-600 mb-1">
                <strong>{t("1. Direct family members")}</strong> {t("Of the PEP, who are spouses, children, spouses of children, parents).")}
              </p>
              <p className="text-sm text-gray-600">
                <strong>
                  {t("2. Associates known to be close to the PEP, which include:")}
                </strong>
              </p>
              <ul className="list-disc ml-6 mt-1 text-sm text-gray-600">
                <li>
                  {t("Individuals having joint ownership rights in a legal person or arrangement or any other close business relationship with the PEP.")}
                </li>
                <li>
                  {t("Individuals having individual ownership rights in a legal person or arrangement established in favour of the PEP.")}
                </li>
              </ul>
            </div>
          </div>

          <Form.Item
            name="isPoliticallyExposedPerson"
            rules={[{ required: true, message: t("Please select an option") }]}
          >
            <Radio.Group onChange={handlePEPChange}>
              <Radio value={true}>{t("Yes")}</Radio>
              <Radio value={false}>{t("No")}</Radio>
            </Radio.Group>
          </Form.Item>

          {isPEP && (
            <Form.Item
              label={t("Please provide details")}
              name="pepDetails"
              rules={[
                { required: true, message: t("Please provide PEP details") },
                { min: 7, message: t("Minimum 7 characters long") },
              ]}
              className="mt-4"
            >
              <TextArea
                rows={4}
                placeholder={t("Please provide details about being a Politically Exposed Person")}
              />
            </Form.Item>
          )}
        </div>

        {/* Upload Documents Section */}
        <div className="max-w-5xl mx-auto border border-gray-300 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">{t("Upload Documents")}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Proof Type Selection */}
            <Form.Item
              label={t("Proof of Identity")}
              name="proofType"
              rules={
                uploadedDocuments.length > 0
                  ? []
                  : [{ required: true, message: t("Please select proof type") }]
              }
            >
              <Select
                placeholder={t("Select Document")}
                options={ProofTypeOptions}
                onChange={handleProofTypeChange}
              />
            </Form.Item>

            {/* Document Sub Type Selection */}
            <Form.Item
              label={t("Document Sub Type")}
              name="documentSubType"
              rules={
                uploadedDocuments.length > 0
                  ? []
                  : [
                      {
                        required: true,
                        message: t("Please select document sub type"),
                      },
                    ]
              }
            >
              <Select
                placeholder={t("Select Document")}
                options={
                  ProofTypeOptions.find((p) => p.value === proofType)
                    ?.subTypeOptions || []
                }
                onChange={handleDocumentSubTypeChange}
                disabled={isDisabled || !proofType}
              />
            </Form.Item>
          </div>

          {/* File Requirements */}
          <p className="text-sm text-red-600 mb-4">
            {t("File Types: .jpg, .pdf, .png, .doc and .docx; max file size 12 Mb.")}
          </p>

          {/* Dragger for Document Upload */}
          <Form.Item
            name="document"
            rules={
              uploadedDocuments.length > 0
                ? []
                : [{ required: true, message: t("Please upload document") }]
            }
          >
            <Dragger
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
              showUploadList={false}
              beforeUpload={() => false}
              fileList={documentFileList}
              onChange={handleDocumentChange}
              disabled={isDisabled || !proofType || !documentSubType}
            >
              <div className="w-full h-full">
                {!previewUrl ? (
                  <div
                    onDragOver={(e) =>
                      documentFileList.length > 0 && e.preventDefault()
                    }
                    onDrop={(e) =>
                      documentFileList.length > 0 && e.preventDefault()
                    }
                  >
                    {DocumentUploadButton}
                  </div>
                ) : (
                  <>
                    <img
                      src={previewUrl}
                      alt="preview"
                      className="h-[200px] w-full object-contain rounded"
                    />
                  </>
                )}
              </div>
            </Dragger>
            {previewUrl && !isDisabled && (
              <span className="flex ml-4 h-12 gap-2">
                <button
                  type="button"
                  onClick={handleAddDocument}
                  className="mt-3 bg-primary text-white px-4 py-2 rounded"
                >
                  {t("Add")}
                </button>
                <button
                  type="button"
                  onClick={handleRemoveDocument}
                  className="mt-3 bg-red-500 text-white px-4 py-2 rounded"
                >
                  {t("Reset")}
                </button>
              </span>
            )}
          </Form.Item>
          {uploadedDocuments.length > 0 && (
            <div className="mt-6">
              <TableWithSkeleton
                columns={columns}
                data={uploadedDocuments}
                loading={false}
                rowKey="id"
              />
            </div>
          )}
        </div>

        <div className="flex justify-between mt-8">
          <Button onClick={onPrevious}>{t("Previous")}</Button>
          {isDisabled ? (
            <Button type="primary" onClick={onNext} disabled={false}>
              {t("Next")}
            </Button>
          ) : (
            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              {isSubmitting ? t("Saving...") : t("Next")}
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default RepresentativeInfo;
